import { Column, Row, Stack } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { ProductDetailStars } from '@/features/shop/components/ProductDetailRatingsAndReviews/ProductDetailStars'
import { useState } from 'react'
import { HDivider } from '@/features/shared/components/Dividers'
import { type ReviewStats } from '@/features/account/services/RatingsAndReviews/types'
import { InView } from '@/features/shared/components/ScrollIntoView'
import { pluralizer } from '@/features/shared/utils/formatter'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { InfoCircleIcon } from '@shipt/design-system-icons'
import { DynamicRatingsAndReviewsInfoModal } from '@/features/shop/components/Modal/RatingsAndReviewsInfoModal/DynamicRatingsAndReviewsInfoModal'
import { IconButton } from '@shipt/design-system-buttons'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { screenSizes } from '@shipt/design-system-themes'

interface Props {
  stats: ReviewStats
}

export const ProductDetailRatingsSummary = ({ stats }: Props) => {
  const [animatedPercentages, setAnimatedPercentages] = useState<{
    [key: string]: number
  }>({})
  const [isVisible, setIsVisible] = useState(false)
  const {
    average_rating,
    rating_distribution,
    recommended_percentage,
    total_reviews,
    total_ratings,
  } = stats
  const { openDialog } = useHandleDialog()

  if (!rating_distribution) return

  const handleInView = () => {
    if (!isVisible) {
      const percentages: Record<number, number> = {}
      for (let i = 1; i <= 5; i++) {
        const count = rating_distribution[i] ?? 0
        percentages[i] = (count / total_ratings) * 100 || 0
      }
      setAnimatedPercentages(percentages)
      setIsVisible(true)
    }
  }

  const pluralizedRatingsText = pluralizer('rating', total_ratings)
  const pluralizedReviewsText = pluralizer('review', total_reviews)

  return (
    <InView onInView={handleInView}>
      <Stack
        direction={{ base: 'column', md: total_reviews > 0 ? 'column' : 'row' }}
        spacing={{ base: 'lg', md: 'xl' }}
      >
        <Column
          spacing="md"
          role="group"
          aria-label={`Average rating: ${average_rating.toFixed(
            1,
          )} out of 5 based on ${total_ratings} ${pluralizedRatingsText}`}
        >
          <Row spacing="sm" align="end">
            <RatingText as="p" size="xl" aria-hidden="true">
              {average_rating.toFixed(1)}
            </RatingText>
            <Body size="md" aria-hidden="true">
              out of 5
            </Body>
          </Row>
          <ProductDetailStars
            rating={average_rating}
            size="lg"
            aria-label={`Average star rating: ${average_rating.toFixed(1)} out of 5`}
          />
          <Row align="center">
            <Body variant="secondary">
              {total_ratings} star {pluralizedRatingsText}
              {total_ratings > total_reviews && ` · ${total_reviews} with ${pluralizedReviewsText}`}
            </Body>
            <IconButton
              size="sm"
              variant="ghost"
              icon={InfoCircleIcon}
              onClick={() => {
                trackElementClicked({
                  display_sublocation: 'reviews',
                  display_format: 'ratings_and_reviews',
                  content: 'info',
                  type: 'button',
                })
                openDialog(DynamicRatingsAndReviewsInfoModal)
              }}
              aria-label="how ratings and reviews work"
            />
          </Row>
        </Column>
        <ProgressContainer role="group" aria-label="Rating distribution">
          {Object.keys(rating_distribution)
            .reverse()
            .map((rating) => {
              const percent = isVisible ? animatedPercentages[rating] || 0 : 0
              return (
                <Row
                  key={rating}
                  marginBottom="md"
                  align="center"
                  role="group"
                  aria-label={`${rating} stars: ${Math.round(percent)}% of ratings`}
                >
                  <RatingLabel>{rating}</RatingLabel>
                  <ProgressWrapper aria-hidden="true">
                    <ProgressInner percentage={percent} />
                  </ProgressWrapper>
                </Row>
              )
            })}
          {total_reviews > 0 && <HorizontalBar />}
        </ProgressContainer>
        {recommended_percentage !== 0 && (
          <Row
            spacing="sm"
            role="group"
            aria-label={`${recommended_percentage}% of reviewers recommend this item`}
          >
            <RecommendText
              size="md"
              surface={recommended_percentage > 79 ? 'inverse' : 'default'}
              strong
              recommendedPercentage={recommended_percentage}
              aria-hidden="true"
            >
              {recommended_percentage}%
            </RecommendText>
            <Body size="md" aria-hidden="true">
              Recommend this item
            </Body>
          </Row>
        )}
      </Stack>
    </InView>
  )
}

const RatingText = styled(Headline)`
  font-size: 40px;
  line-height: 40px;
  font-weight: 500;
  position: relative;
`

const RecommendText = styled(Body)<{ recommendedPercentage: number }>`
  background-color: ${({ recommendedPercentage, theme }) =>
    recommendedPercentage > 79
      ? theme.color.background.feedback.positive.loud
      : theme.color.background.base.neutral.subtle_00};
  border-radius: 4px;
  padding: 0 4px;
  border: 1px solid
    ${({ recommendedPercentage, theme }) => (recommendedPercentage > 79 ? 'none' : theme.gray300)};
  height: 1.3125rem;
`

const ProgressContainer = styled.div`
  max-width: 25rem;
`

const ProgressBase = styled.div`
  height: 0.5rem;
  border-radius: 6px;
`

const ProgressWrapper = styled(ProgressBase)`
  background-color: ${({ theme }) => theme.gray300};
  width: 20.5625rem;
  min-height: 0.5rem;
`

const ProgressInner = styled(ProgressBase)<{ percentage: number }>`
  background-color: ${({ theme }) => theme.plum};
  width: ${(props) => props.percentage}%;
  transition: width 1s ease-in-out;
`

const RatingLabel = styled.span`
  margin-right: 0.3125rem;
  min-width: 0.625rem;
  text-align: left;
`

const HorizontalBar = styled(HDivider)`
  @media ${screenSizes.max_mobile} {
    margin-top: 1rem;
  }

  margin-top: 1.5rem;
  width: 21.5rem;
  margin-left: 0;
  margin-bottom: 0;
`
